import { useDomainFromUrl } from '@/app/_common/domain/use-domain-from-url'
import { taskManagerApiPaths } from '@/hooks/api/_paths'
import type { Message } from '@/hooks/api/_types'
import { useApiRequest } from '@/lib/http-utils'
import { useQuery, type UseQueryOptions } from '@tanstack/react-query'
import { TurnStatus } from './use-turn-status-query'

type MessagesAPIResponse = {
  workflowId: string
  title: string | null
  status: TurnStatus
  prompt: string
  startEpoch: number
  recentTurnStartTimeEpoch?: number | null
  clientId: string
  pinned?: boolean
  turnIds: string[] | null
  error?: {
    errorCode: string
    status: number
  } | null
  messages: Message[] | null
}

function useMessagesQuery(
  { workflowId }: { workflowId: string },
  queryOptions: Omit<UseQueryOptions<MessagesAPIResponse>, 'queryKey' | 'queryFn'>,
) {
  const { domainId } = useDomainFromUrl()
  const fetchAndParseMessages = useFetchAndParseMessages()
  return useQuery({
    queryKey: messagesKeyFactory({ workflowId, domainId }),
    queryFn: () => fetchAndParseMessages({ workflowId }),
    ...queryOptions,
  })
}

const useFetchAndParseMessages = () => {
  const { apiRequest, domainId } = useApiRequest()
  return async ({ workflowId }: { workflowId: string }) => {
    const response = await apiRequest({
      path: taskManagerApiPaths.workflowMessages({ workflowId, domainId }),
    })

    if (!response.ok) throw new Error('FAILED_TO_GET_WORKFLOW_MESSAGES')

    const json = (await response.json()) as MessagesAPIResponse

    return json
  }
}
// 👇 key factory

const messagesKeyFactory = ({ workflowId, domainId }: { workflowId: string; domainId: string }) => [
  'messages',
  workflowId,
  domainId,
]
// 👇 types

export { messagesKeyFactory, useFetchAndParseMessages, useMessagesQuery, type MessagesAPIResponse }
